import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Logo } from "@/components/Layout";
import PowerStudiesAdmin from "@/pages/admin/PowerStudiesAdmin";
import TemplatesAdmin from "@/pages/admin/TemplatesAdmin";
import {
  LayoutDashboard, Rss, Newspaper, Users, Megaphone, Mail, ScrollText, Map,
  RefreshCw, Play, Pause, Trash2, CheckCircle2, Download, LogOut, Send, Eye, Zap, FileStack,
} from "lucide-react";

const TABS = [
  ["overview", "Overview", LayoutDashboard], ["sources", "Sources", Rss],
  ["news", "Newsroom", Newspaper], ["states", "States", Map],
  ["subscribers", "Subscribers", Users], ["newsletters", "Newsletters", Mail],
  ["ads", "Advertising", Megaphone], ["studies", "Power Studies", Zap],
  ["templates", "Templates", FileStack], ["audit", "Audit & Logs", ScrollText],
];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("dyor_token")}` });

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => { if (!loading && user?.role !== "admin") navigate("/admin"); }, [user, loading, navigate]);
  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-slate-400 font-mono">Loading…</div>;

  return (
    <div className="min-h-screen flex">
      <aside className="w-16 sm:w-60 border-r border-white/10 bg-[#0B0F17] flex flex-col shrink-0">
        <div className="h-16 flex items-center px-4 border-b border-white/10"><Logo /></div>
        <nav className="flex-1 p-2 space-y-1">
          {TABS.map(([id, label, Icon]) => (
            <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${tab === id ? "bg-[#2563EB] text-white" : "text-slate-400 hover:bg-slate-800 hover:text-white"}`}>
              <Icon size={17} /> <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </nav>
        <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin"); }}
          className="flex items-center gap-3 px-5 py-4 text-slate-400 hover:text-white border-t border-white/10 text-sm">
          <LogOut size={17} /> <span className="hidden sm:inline">Sign out</span>
        </button>
      </aside>
      <main className="flex-1 overflow-x-hidden">
        <div className="max-w-6xl mx-auto p-5 sm:p-8">
          {tab === "overview" && <Overview />}
          {tab === "sources" && <Sources />}
          {tab === "news" && <NewsAdmin />}
          {tab === "states" && <StatesAdmin />}
          {tab === "subscribers" && <Subscribers />}
          {tab === "newsletters" && <Newsletters />}
          {tab === "ads" && <Ads />}
          {tab === "studies" && <PowerStudiesAdmin />}
          {tab === "templates" && <TemplatesAdmin />}
          {tab === "audit" && <Audit />}
        </div>
      </main>
    </div>
  );
}

const H = ({ children, sub }) => (<div className="mb-6"><h1 className="font-display font-bold text-2xl text-white">{children}</h1>{sub && <p className="text-slate-400 text-sm mt-1">{sub}</p>}</div>);

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <p className="text-slate-400 font-mono">Loading…</p>;
  const cards = [
    ["States", stats.states], ["Active sources", stats.sources_active], ["Published news", stats.news_published],
    ["Awaiting review", stats.news_review], ["Subscribers", stats.subscribers], ["Confirmed", stats.subscribers_confirmed],
    ["Ad inquiries", stats.ad_inquiries], ["Newsletters", stats.newsletters],
  ];
  return (
    <div>
      <H sub="Platform health and content at a glance.">Dashboard</H>
      {!stats.email_configured && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 mb-6 text-amber-200 text-sm" data-testid="email-config-warning">
          <strong>Email delivery not fully configured.</strong> Confirmation emails and newsletter sends require a verified sending domain. Signups are still stored; nothing is faked.
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(([l, v]) => (
          <div key={l} className="card p-5" data-testid={`stat-${l}`}>
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{l}</p>
            <p className="font-display font-black text-3xl text-white mt-1">{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Sources() {
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState({ name: "", feed_url: "", type: "rss", topics: "", status: "active" });
  const load = useCallback(() => api.get("/admin/sources").then((r) => setSources(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    await api.post("/admin/sources", { ...form, topics: form.topics.split(",").map((t) => t.trim()).filter(Boolean) });
    setForm({ name: "", feed_url: "", type: "rss", topics: "", status: "active" }); toast.success("Source added"); load();
  };
  const toggle = async (id) => { await api.post(`/admin/sources/${id}/toggle`); load(); };
  const del = async (id) => { await api.delete(`/admin/sources/${id}`); toast.success("Removed"); load(); };
  const crawl = async () => { await api.post("/admin/crawl/run"); toast.success("Crawl started — check back in a moment"); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <H sub="Approved RSS/API sources. Pause, retry, or remove. Respect each source's access rules.">News sources</H>
        <button data-testid="run-crawl-btn" onClick={crawl} className="inline-flex items-center gap-2 bg-[#84CC16] text-[#0B0F17] font-bold px-4 py-2.5 rounded-lg text-sm"><RefreshCw size={15} /> Run crawl now</button>
      </div>
      <form onSubmit={add} data-testid="add-source-form" className="card p-5 mb-6 grid sm:grid-cols-5 gap-3">
        <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500" data-testid="source-name" />
        <input required placeholder="Feed/API URL" value={form.feed_url} onChange={(e) => setForm({ ...form, feed_url: e.target.value })} className="sm:col-span-2 bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500" data-testid="source-url" />
        <input placeholder="topics (comma)" value={form.topics} onChange={(e) => setForm({ ...form, topics: e.target.value })} className="bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500" data-testid="source-topics" />
        <button type="submit" data-testid="add-source-btn" className="bg-[#2563EB] text-white font-semibold rounded-lg text-sm">Add</button>
      </form>
      <div className="space-y-3">
        {sources.map((s) => (
          <div key={s.id} data-testid={`source-row-${s.id}`} className="card p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2"><span className="font-semibold text-white">{s.name}</span>
                <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${s.status === "active" ? "bg-[#84CC16]/20 text-[#84CC16]" : "bg-slate-700 text-slate-300"}`}>{s.status}</span></div>
              <p className="text-slate-500 text-xs truncate">{s.feed_url}</p>
              <p className="text-slate-500 text-[11px] font-mono mt-1">last success: {s.last_success ? new Date(s.last_success).toLocaleString() : "never"} · items: {s.item_count}{s.last_error ? ` · error: ${s.last_error.slice(0, 40)}` : ""}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`toggle-source-${s.id}`} onClick={() => toggle(s.id)} className="p-2 rounded-lg border border-white/10 text-slate-300 hover:text-white">{s.status === "active" ? <Pause size={15} /> : <Play size={15} />}</button>
              <button data-testid={`delete-source-${s.id}`} onClick={() => del(s.id)} className="p-2 rounded-lg border border-white/10 text-slate-300 hover:text-red-400"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NewsAdmin() {
  const [items, setItems] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [filter, setFilter] = useState("");
  const loadItems = useCallback(() => api.get(`/admin/news${filter ? `?status=${filter}` : ""}`).then((r) => setItems(r.data)), [filter]);
  const loadRejected = useCallback(() => api.get("/admin/rejected-news").then((r) => setRejected(r.data)), []);
  useEffect(() => { if (filter === "rejected") loadRejected(); else loadItems(); }, [filter, loadItems, loadRejected]);

  const publish = async (id) => { await api.post(`/admin/news/${id}/publish`); toast.success("Published"); loadItems(); };
  const remove = async (id) => { await api.delete(`/admin/news/${id}`); toast.success("Removed"); loadItems(); };
  const saveSummary = async (id, summary) => { await api.put(`/admin/news/${id}`, { summary }); toast.success("Summary updated"); loadItems(); };
  const delRejected = async (id) => { await api.delete(`/admin/rejected-news/${id}`); toast.success("Deleted"); loadRejected(); };
  const restoreRejected = async (id) => { await api.post(`/admin/rejected-news/${id}/restore`); toast.success("Restored to review queue"); loadRejected(); };

  return (
    <div>
      <H sub="Only stories that pass the relevance filter are published; off-topic items are logged under 'Filtered out' for your review.">Newsroom</H>
      <div className="flex gap-2 mb-5 flex-wrap">
        {[["", "All"], ["review", "Needs review"], ["published", "Published"], ["removed", "Removed"], ["rejected", "Filtered out"]].map(([v, l]) => (
          <button key={v} data-testid={`news-filter-${v || "all"}`} onClick={() => setFilter(v)} className={`text-sm px-3 py-2 rounded-lg border ${filter === v ? "bg-[#2563EB] border-[#2563EB] text-white" : "border-white/10 text-slate-300"}`}>{l}</button>
        ))}
      </div>

      {filter === "rejected" ? (
        <div className="space-y-3" data-testid="rejected-list">
          {rejected.length === 0 && <p className="text-slate-400 text-sm">No filtered-out stories. The relevance filter hasn't rejected anything yet.</p>}
          {rejected.map((n) => (
            <div key={n.id} data-testid={`rejected-${n.id}`} className="card p-4">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">filtered</span>
                <span className="text-slate-500 text-xs">{n.publisher}</span>
                <span className="font-mono text-[10px] text-slate-500">relevance {n.relevance}</span>
                <span className="font-mono text-[10px] text-amber-400">reason: {n.reason}</span>
              </div>
              <a href={n.url} target="_blank" rel="noreferrer" className="font-semibold text-white hover:text-[#84CC16]">{n.title}</a>
              <div className="flex gap-2 mt-2">
                <button data-testid={`restore-${n.id}`} onClick={() => restoreRejected(n.id)} className="text-xs border border-white/10 text-slate-300 hover:text-[#84CC16] px-3 py-1.5 rounded-lg">Keep anyway (send to review)</button>
                <button data-testid={`del-rejected-${n.id}`} onClick={() => delRejected(n.id)} className="inline-flex items-center gap-1.5 text-xs border border-white/10 text-slate-300 hover:text-red-400 px-3 py-1.5 rounded-lg"><Trash2 size={13} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {items.length === 0 && <p className="text-slate-400 text-sm">No items. Run a crawl from the Sources tab.</p>}
          {items.map((n) => (
            <div key={n.id} data-testid={`news-admin-${n.id}`} className="card p-4">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${n.status === "published" ? "bg-[#84CC16]/20 text-[#84CC16]" : n.status === "review" ? "bg-amber-500/20 text-amber-400" : "bg-slate-700 text-slate-300"}`}>{n.status}</span>
                <span className="text-slate-500 text-xs">{n.publisher}</span>
                {(n.topics || []).slice(0, 4).map((t) => <span key={t} className="text-[10px] text-slate-400 bg-slate-800 rounded px-1.5 py-0.5">{t}</span>)}
                {n.ai_generated && <span className="text-[10px] text-slate-500 border border-white/10 rounded px-1">AI</span>}
              </div>
              <a href={n.url} target="_blank" rel="noreferrer" className="font-semibold text-white hover:text-[#84CC16]">{n.title}</a>
              <textarea defaultValue={n.summary || ""} data-testid={`news-summary-${n.id}`} rows={2} placeholder="No summary"
                onBlur={(e) => e.target.value !== (n.summary || "") && saveSummary(n.id, e.target.value)}
                className="w-full mt-2 bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2 text-slate-300 text-sm focus:outline-none focus:border-blue-500" />
              <div className="flex gap-2 mt-2">
                {n.status !== "published" && <button data-testid={`publish-news-${n.id}`} onClick={() => publish(n.id)} className="inline-flex items-center gap-1.5 text-xs bg-[#84CC16] text-[#0B0F17] font-semibold px-3 py-1.5 rounded-lg"><CheckCircle2 size={13} /> Publish</button>}
                <button data-testid={`remove-news-${n.id}`} onClick={() => remove(n.id)} className="inline-flex items-center gap-1.5 text-xs border border-white/10 text-slate-300 hover:text-red-400 px-3 py-1.5 rounded-lg"><Trash2 size={13} /> Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatesAdmin() {
  const [states, setStates] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/states").then((r) => setStates(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const { abbr, facility_count, power_capacity_mw, water_use, cooling_systems, summary, data_confidence } = edit;
    await api.put(`/admin/states/${abbr}`, { facility_count, power_capacity_mw, water_use, cooling_systems, summary, data_confidence });
    toast.success("State updated"); setEdit(null); load();
  };
  const exportCsv = () => window.open(`${API}/admin/states/export`, "_blank");

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <H sub="Edit reported research data. Never invent totals without a cited methodology.">States</H>
        <button data-testid="export-states" onClick={exportCsv} className="inline-flex items-center gap-2 border border-white/10 text-slate-300 px-4 py-2.5 rounded-lg text-sm"><Download size={15} /> Export CSV</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {states.map((s) => (
          <button key={s.id} data-testid={`admin-state-${s.abbr}`} onClick={() => setEdit({ ...s })} className="card p-4 text-left">
            <div className="flex justify-between"><span className="font-semibold text-white">{s.name}</span><span className="font-mono text-xs text-slate-500">{s.abbr}</span></div>
            <p className="text-xs text-slate-500 mt-1">{s.data_confidence}</p>
          </button>
        ))}
      </div>
      {edit && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setEdit(null)}>
          <div className="card p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto bg-[#111827]" onClick={(e) => e.stopPropagation()} data-testid="state-edit-modal">
            <h3 className="font-display font-bold text-xl text-white mb-4">{edit.name}</h3>
            {[["facility_count", "Facility count"], ["power_capacity_mw", "Power capacity"], ["water_use", "Water use"], ["cooling_systems", "Cooling systems"]].map(([k, l]) => (
              <div key={k} className="mb-3"><label className="text-xs text-slate-400">{l}</label>
                <input value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} className="w-full mt-1 bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500" /></div>
            ))}
            <div className="mb-3"><label className="text-xs text-slate-400">Summary</label>
              <textarea value={edit.summary || ""} rows={3} onChange={(e) => setEdit({ ...edit, summary: e.target.value })} className="w-full mt-1 bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500" /></div>
            <div className="mb-4"><label className="text-xs text-slate-400">Confidence</label>
              <select value={edit.data_confidence} onChange={(e) => setEdit({ ...edit, data_confidence: e.target.value })} className="w-full mt-1 bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                {["verified", "reported", "estimated", "modeled", "unknown"].map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
            <div className="flex gap-2"><button data-testid="save-state" onClick={save} className="flex-1 bg-[#2563EB] text-white font-semibold py-2.5 rounded-lg">Save</button>
              <button onClick={() => setEdit(null)} className="px-4 border border-white/10 text-slate-300 rounded-lg">Cancel</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function Subscribers() {
  const [subs, setSubs] = useState([]);
  const [q, setQ] = useState("");
  const [aud, setAud] = useState("");
  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q); if (aud) p.set("audience", aud);
    api.get(`/admin/subscribers?${p}`).then((r) => setSubs(r.data));
  }, [q, aud]);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { await api.delete(`/admin/subscribers/${id}`); load(); };
  const exportCsv = () => window.open(`${API}/admin/subscribers/export`, "_blank");

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <H sub="Search, segment, and export. We never sell or rent this list.">Subscribers</H>
        <button data-testid="export-subs" onClick={exportCsv} className="inline-flex items-center gap-2 border border-white/10 text-slate-300 px-4 py-2.5 rounded-lg text-sm"><Download size={15} /> Export CSV</button>
      </div>
      <div className="flex gap-3 mb-5">
        <input data-testid="sub-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email…" className="flex-1 bg-[#0B0F17] border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500" />
        <select data-testid="sub-audience-filter" value={aud} onChange={(e) => setAud(e.target.value)} className="bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm">
          <option value="">All audiences</option>{["resident", "customer", "operator", "developer", "utility", "public_official", "journalist", "other"].map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>
      <div className="card overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead><tr className="border-b border-white/10 text-left"><th className="p-3 text-slate-400 font-mono text-xs">Email</th><th className="p-3 text-slate-400 font-mono text-xs">Audience</th><th className="p-3 text-slate-400 font-mono text-xs">State</th><th className="p-3 text-slate-400 font-mono text-xs">Status</th><th className="p-3"></th></tr></thead>
          <tbody>
            {subs.map((s) => (
              <tr key={s.id} data-testid={`sub-row-${s.id}`} className="border-b border-white/5">
                <td className="p-3 text-white">{s.email}</td><td className="p-3 text-slate-300">{s.audience_type}</td><td className="p-3 text-slate-300">{s.state || "—"}</td>
                <td className="p-3">{s.unsubscribed ? <span className="text-red-400 text-xs">unsubscribed</span> : s.confirmed ? <span className="text-[#84CC16] text-xs">confirmed</span> : <span className="text-amber-400 text-xs">pending</span>}</td>
                <td className="p-3 text-right"><button onClick={() => del(s.id)} className="text-slate-400 hover:text-red-400"><Trash2 size={15} /></button></td>
              </tr>
            ))}
            {subs.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-400">No subscribers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Newsletters() {
  const [list, setList] = useState([]);
  const [preview, setPreview] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const load = useCallback(() => api.get("/admin/newsletters").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const generate = async (type) => { await api.post("/admin/newsletters/generate", { edition_type: type }); toast.success("Draft generated"); load(); };
  const approve = async (id) => { await api.post(`/admin/newsletters/${id}/approve`); toast.success("Approved"); load(); };
  const send = async (id) => { const { data } = await api.post(`/admin/newsletters/${id}/send`); toast[data.status.includes("not_configured") ? "warning" : "success"](data.message || "Sending started"); load(); };
  const testSend = async (id) => { if (!testEmail) return toast.error("Enter a test email"); const { data } = await api.post(`/admin/newsletters/${id}/test-send`, { email: testEmail }); toast[data.status === "sent" ? "success" : "warning"](data.message || `Test send: ${data.status}`); };

  return (
    <div>
      <H sub="Generate a draft, preview, send a test, approve, then send. Nothing sends without your approval.">Newsletters</H>
      <div className="flex gap-3 mb-6">
        <button data-testid="gen-citizen" onClick={() => generate("citizen")} className="bg-[#84CC16] text-[#0B0F17] font-bold px-4 py-2.5 rounded-lg text-sm">Generate citizen draft</button>
        <button data-testid="gen-professional" onClick={() => generate("professional")} className="bg-[#2563EB] text-white font-bold px-4 py-2.5 rounded-lg text-sm">Generate professional draft</button>
      </div>
      <div className="flex gap-2 mb-6">
        <input data-testid="test-email-input" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@email.com (for test sends)" className="flex-1 bg-[#0B0F17] border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500" />
      </div>
      <div className="space-y-3">
        {list.map((n) => (
          <div key={n.id} data-testid={`newsletter-${n.id}`} className="card p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <span className="font-semibold text-white">{n.subject}</span>
                <span className={`ml-2 font-mono text-[10px] px-1.5 py-0.5 rounded ${n.status === "sent" ? "bg-[#84CC16]/20 text-[#84CC16]" : n.status === "approved" ? "bg-blue-500/20 text-blue-400" : "bg-slate-700 text-slate-300"}`}>{n.status}</span>
                {n.auto_generated && <span className="ml-1 text-[10px] text-slate-500">auto</span>}
                <p className="text-slate-500 text-xs mt-1">{new Date(n.created_at).toLocaleString()}{n.recipients ? ` · sent to ${n.recipients}` : ""}</p>
              </div>
              <div className="flex gap-2">
                <button data-testid={`preview-${n.id}`} onClick={() => setPreview(n)} className="inline-flex items-center gap-1.5 text-xs border border-white/10 text-slate-300 px-3 py-1.5 rounded-lg"><Eye size={13} /> Preview</button>
                <button data-testid={`test-${n.id}`} onClick={() => testSend(n.id)} className="inline-flex items-center gap-1.5 text-xs border border-white/10 text-slate-300 px-3 py-1.5 rounded-lg"><Send size={13} /> Test</button>
                {n.status === "draft" && <button data-testid={`approve-${n.id}`} onClick={() => approve(n.id)} className="text-xs bg-[#2563EB] text-white font-semibold px-3 py-1.5 rounded-lg">Approve</button>}
                {n.status === "approved" && <button data-testid={`send-${n.id}`} onClick={() => send(n.id)} className="text-xs bg-[#84CC16] text-[#0B0F17] font-semibold px-3 py-1.5 rounded-lg">Send</button>}
              </div>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-slate-400 text-sm">No editions yet. Generate one above.</p>}
      </div>
      {preview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="newsletter-preview">
            <div dangerouslySetInnerHTML={{ __html: preview.preview_html }} style={{ background: "#0B0F17" }} />
          </div>
        </div>
      )}
    </div>
  );
}

function Ads() {
  const [sub, setSub] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [packages, setPackages] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [edit, setEdit] = useState(null);

  const loadOrders = useCallback(() => api.get("/admin/ad-orders").then((r) => setOrders(r.data)), []);
  const loadPackages = useCallback(() => api.get("/admin/ad-packages").then((r) => setPackages(r.data)), []);
  useEffect(() => { loadOrders(); loadPackages(); api.get("/admin/ad-inquiries").then((r) => setInquiries(r.data)); }, [loadOrders, loadPackages]);

  const setStatus = async (id, status) => { await api.put(`/admin/ad-orders/${id}/status`, { status }); toast.success(`Marked ${status}`); loadOrders(); };
  const savePackage = async () => {
    const b = { ...edit, price_amount: edit.price_amount === "" || edit.price_amount == null ? null : Number(edit.price_amount),
      included: Array.isArray(edit.included) ? edit.included : String(edit.included || "").split("\n").map((s) => s.trim()).filter(Boolean),
      not_included: Array.isArray(edit.not_included) ? edit.not_included : String(edit.not_included || "").split("\n").map((s) => s.trim()).filter(Boolean) };
    await api.put(`/admin/ad-packages/${edit.id}`, b);
    toast.success("Package saved"); setEdit(null); loadPackages();
  };

  const STATUS_CLS = { requested: "bg-amber-500/20 text-amber-400", contacted: "bg-blue-500/20 text-blue-400", approved: "bg-indigo-500/20 text-indigo-300", active: "bg-[#84CC16]/20 text-[#84CC16]", declined: "bg-red-500/20 text-red-400" };

  return (
    <div>
      <H sub="Review self-serve requests, manage central pricing, and view inquiries. Nothing is activated until you approve it.">Advertising</H>
      <div className="flex gap-2 mb-6">
        {[["orders", "Requests"], ["packages", "Pricing / Packages"], ["inquiries", "Inquiries"]].map(([id, label]) => (
          <button key={id} data-testid={`ads-sub-${id}`} onClick={() => setSub(id)}
            className={`text-sm px-4 py-2 rounded-lg border ${sub === id ? "bg-[#2563EB] border-[#2563EB] text-white" : "border-white/10 text-slate-300"}`}>{label}</button>
        ))}
      </div>

      {sub === "orders" && (
        <div className="space-y-3">
          {orders.length === 0 && <p className="text-slate-400 text-sm">No requests yet.</p>}
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="card p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <span className="font-semibold text-white">{o.company}</span>
                  <span className={`ml-2 font-mono text-[10px] px-1.5 py-0.5 rounded ${STATUS_CLS[o.status] || "bg-slate-700 text-slate-300"}`}>{o.status}</span>
                  <p className="text-slate-400 text-xs mt-1">{o.package_name} · {o.displayed_price} · {new Date(o.created_at).toLocaleString()}</p>
                  <p className="text-slate-500 text-xs">{o.contact_name} · {o.contact_email}{o.contact_phone ? ` · ${o.contact_phone}` : ""}</p>
                  {o.notes && <p className="text-slate-400 text-sm mt-1">“{o.notes}”</p>}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {["contacted", "approved", "active", "declined"].map((s) => (
                    <button key={s} data-testid={`order-${o.id}-${s}`} onClick={() => setStatus(o.id, s)}
                      className="text-[11px] border border-white/10 text-slate-300 hover:text-white px-2.5 py-1 rounded-lg">{s}</button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {sub === "packages" && (
        <div className="space-y-3" data-testid="packages-admin">
          <p className="text-slate-400 text-sm mb-1">Edit prices here — this is the single place that controls what shows on the pricing page. Toggle off "Placeholder" once a price is final.</p>
          {packages.map((p) => (
            <div key={p.id} className="card p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`pkg-${p.id}`}>
              <div>
                <span className="font-semibold text-white">{p.name}</span>
                {p.is_placeholder && <span className="ml-2 font-mono text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">placeholder</span>}
                <span className="ml-2 font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">{p.availability}</span>
                <p className="text-slate-400 text-xs mt-1">{p.price_amount != null ? `$${Number(p.price_amount).toLocaleString()}` : "—"} {p.billing_interval || ""} · {p.fee_type === "one_time" ? "one-time" : "recurring"} · {p.category}</p>
              </div>
              <button data-testid={`edit-pkg-${p.id}`} onClick={() => setEdit({ ...p, included: (p.included || []).join("\n"), not_included: (p.not_included || []).join("\n") })}
                className="text-xs bg-[#2563EB] text-white font-semibold px-3 py-1.5 rounded-lg">Edit price &amp; details</button>
            </div>
          ))}
        </div>
      )}

      {sub === "inquiries" && (
        <div className="space-y-3">
          {inquiries.map((i) => (
            <div key={i.id} data-testid={`inquiry-${i.id}`} className="card p-4">
              <div className="flex justify-between flex-wrap gap-2"><span className="font-semibold text-white">{i.name} · {i.company || "—"}</span><span className="text-slate-500 text-xs">{new Date(i.created_at).toLocaleDateString()}</span></div>
              <p className="text-slate-400 text-xs">{i.email} · interested in: {i.placement_interest || "—"} · budget: {i.budget || "—"}</p>
              <p className="text-slate-300 text-sm mt-2">{i.message}</p>
            </div>
          ))}
          {inquiries.length === 0 && <p className="text-slate-400 text-sm">No inquiries yet.</p>}
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setEdit(null)}>
          <div className="card p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto bg-[#111827]" onClick={(e) => e.stopPropagation()} data-testid="pkg-edit-modal">
            <h3 className="font-display font-bold text-xl text-white mb-4">{edit.name}</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div><label className="text-xs text-slate-400">Price (number)</label>
                <input data-testid="pkg-price" type="number" value={edit.price_amount ?? ""} onChange={(e) => setEdit({ ...edit, price_amount: e.target.value })}
                  className="w-full mt-1 bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500" /></div>
              <div><label className="text-xs text-slate-400">Billing interval</label>
                <input value={edit.billing_interval || ""} onChange={(e) => setEdit({ ...edit, billing_interval: e.target.value })} placeholder="per month"
                  className="w-full mt-1 bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500" /></div>
              <div><label className="text-xs text-slate-400">Fee type</label>
                <select value={edit.fee_type} onChange={(e) => setEdit({ ...edit, fee_type: e.target.value })} className="w-full mt-1 bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                  <option value="recurring">recurring</option><option value="one_time">one_time</option></select></div>
              <div><label className="text-xs text-slate-400">Availability</label>
                <select value={edit.availability} onChange={(e) => setEdit({ ...edit, availability: e.target.value })} className="w-full mt-1 bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                  {["available", "limited", "sold_out", "coming_soon"].map((a) => <option key={a} value={a}>{a}</option>)}</select></div>
            </div>
            <div className="mb-3"><label className="text-xs text-slate-400">Duration</label>
              <input value={edit.duration || ""} onChange={(e) => setEdit({ ...edit, duration: e.target.value })} className="w-full mt-1 bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500" /></div>
            <div className="mb-3"><label className="text-xs text-slate-400">What's included (one per line)</label>
              <textarea data-testid="pkg-included" rows={3} value={edit.included} onChange={(e) => setEdit({ ...edit, included: e.target.value })} className="w-full mt-1 bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500" /></div>
            <div className="mb-3"><label className="text-xs text-slate-400">Not included (one per line)</label>
              <textarea rows={2} value={edit.not_included} onChange={(e) => setEdit({ ...edit, not_included: e.target.value })} className="w-full mt-1 bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500" /></div>
            <div className="mb-3"><label className="text-xs text-slate-400">Cancellation terms</label>
              <textarea rows={2} value={edit.cancellation_terms || ""} onChange={(e) => setEdit({ ...edit, cancellation_terms: e.target.value })} className="w-full mt-1 bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500" /></div>
            <div className="flex items-center gap-4 mb-4">
              <label className="flex items-center gap-2 text-sm text-slate-300"><input data-testid="pkg-placeholder" type="checkbox" checked={edit.is_placeholder} onChange={(e) => setEdit({ ...edit, is_placeholder: e.target.checked })} className="w-4 h-4 accent-[#84CC16]" /> Placeholder price</label>
              <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} className="w-4 h-4 accent-[#84CC16]" /> Active</label>
            </div>
            <div className="flex gap-2"><button data-testid="save-pkg" onClick={savePackage} className="flex-1 bg-[#2563EB] text-white font-semibold py-2.5 rounded-lg">Save</button>
              <button onClick={() => setEdit(null)} className="px-4 border border-white/10 text-slate-300 rounded-lg">Cancel</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function Audit() {
  const [logs, setLogs] = useState([]);
  const [crawls, setCrawls] = useState([]);
  const [delivery, setDelivery] = useState([]);
  useEffect(() => {
    api.get("/admin/audit").then((r) => setLogs(r.data));
    api.get("/admin/crawler-logs").then((r) => setCrawls(r.data));
    api.get("/admin/delivery-log").then((r) => setDelivery(r.data));
  }, []);
  return (
    <div>
      <H sub="Crawler health, email delivery log, and an audit trail of edits.">Audit & Logs</H>
      <h3 className="font-display font-semibold text-white mb-3">Crawler runs</h3>
      <div className="space-y-2 mb-8">
        {crawls.map((c) => (
          <div key={c.id} className="card p-3 text-sm flex flex-wrap gap-x-6 gap-y-1" data-testid={`crawl-log-${c.id}`}>
            <span className="text-slate-400">{new Date(c.finished_at).toLocaleString()}</span>
            <span className="text-white">+{c.items_added} items</span>
            <span className="text-slate-400">{c.sources_checked} sources</span>
            <span className="text-slate-400">{c.summaries_generated} AI summaries</span>
            {c.errors?.length > 0 && <span className="text-amber-400">{c.errors.length} errors</span>}
          </div>
        ))}
        {crawls.length === 0 && <p className="text-slate-400 text-sm">No crawl runs yet.</p>}
      </div>
      <h3 className="font-display font-semibold text-white mb-3">Email delivery log</h3>
      <div className="space-y-2 mb-8">
        {delivery.map((d) => (<div key={d.id} className="card p-3 text-sm flex gap-4"><span className="text-slate-400">{new Date(d.sent_at).toLocaleString()}</span><span className="text-white">{d.to}</span><span className="text-slate-500">{d.type}</span><span className={d.email_id ? "text-[#84CC16]" : "text-red-400"}>{d.email_id ? "sent" : "not sent"}</span></div>))}
        {delivery.length === 0 && <p className="text-slate-400 text-sm">No emails sent yet.</p>}
      </div>
      <h3 className="font-display font-semibold text-white mb-3">Audit trail</h3>
      <div className="space-y-2">
        {logs.map((l) => (<div key={l.id} className="card p-3 text-sm flex flex-wrap gap-x-4" data-testid={`audit-${l.id}`}><span className="text-slate-400 font-mono text-xs">{new Date(l.timestamp).toLocaleString()}</span><span className="text-[#84CC16]">{l.actor}</span><span className="text-white">{l.action}</span><span className="text-slate-300">{l.entity}</span>{l.details && <span className="text-slate-500 truncate">{l.details}</span>}</div>))}
        {logs.length === 0 && <p className="text-slate-400 text-sm">No edits logged yet.</p>}
      </div>
    </div>
  );
}
